import { PortalBalancesRow } from '@/components/portal-modules/PortalBalancesTable';
import { currencyConfigDict } from '@/config/currency';
import { getCurrency } from '@/utils/hooks/useCurrency';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import { CellContext } from '@tanstack/react-table';
import cn from 'classnames';
import { MdArrowCircleDown, MdArrowCircleUp, MdChevronRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { generalAmountCell } from './currencyHelper';

interface PortalBalancesColumnsParams {
    lang: UseLanguageReturn;
    formatQty: Intl.NumberFormat;
    formatDate: Intl.DateTimeFormat;
    baseCurrency: string;
    totalInBaseCurrency?: number;
    isCryptoDepositWithdrawalEnabled?: boolean;
    onDeposit?: (currency: string) => void;
    onWithdraw?: (currency: string) => void;
}

export const portalBalancesColumns = (params: PortalBalancesColumnsParams) => {
    const {
        lang,
        formatQty,
        formatDate,
        baseCurrency,
        totalInBaseCurrency,
        isCryptoDepositWithdrawalEnabled,
        onDeposit,
        onWithdraw
    } = params;

    const columns = [
        {
            accessorKey: 'tableCurrency',
            header: ({ table }) => {
                return (
                    <div className="flex space-x-1 items-center truncate">
                        <button
                            onClick={(e) => {
                                table.toggleAllRowsExpanded();
                                e.stopPropagation();
                            }}
                            title={table.getIsAllRowsExpanded() ? 'Collapse all rows' : 'Expand all rows'}
                            className="relative transform scale-125 left-0">
                            <MdChevronRight
                                className={cn('h-3.5 w-3.5 font-bold text-green duration-200', {
                                    ['rotate-90 ']: table.getIsAllRowsExpanded()
                                })}
                                aria-hidden
                            />
                        </button>

                        <span className="truncate">{lang.lite.currency}</span>
                    </div>
                );
            },
            cell: ({ row, getValue }: CellContext<PortalBalancesRow, any>) => {
                const value = getValue();
                const { show } = value;
                return (
                    <div
                        className="flex items-center gap-1"
                        style={{
                            paddingLeft: `${row.getCanExpand() ? row.depth * 1 : row.depth * 1.5}rem`
                        }}>
                        <div className={`flex space-x-1`}>
                            {row.getCanExpand() && (
                                <button title={row.getIsExpanded() ? 'Collapse current row' : 'Expand current row'}>
                                    <MdChevronRight
                                        className={cn('h-3.5 w-3.5 text-green duration-200', {
                                            ['rotate-90']: row.getIsExpanded()
                                        })}
                                        aria-hidden
                                    />
                                </button>
                            )}

                            {row.depth === 0 && show}
                            {row.depth > 0 && `VD: ${formatDate.format(new Date(row.original.currencyName || ''))}`}
                        </div>
                    </div>
                );
            },
            footer: () => {
                return (
                    <div className={`px-2 py-1.5 text-xs lg:text-sm flex items-start`}>
                        <span className="font-semibold">{`${lang.balances.total} ${
                            baseCurrency ? `(${baseCurrency})` : ''
                        } : `}</span>
                    </div>
                );
            }
        },
        {
            accessorKey: 'amount',
            header: lang.balances.balance,
            cell: ({ row }: CellContext<PortalBalancesRow, any>) => {
                const amount = row.original.amount;
                const { show } = row.original.tableCurrency;

                return generalAmountCell({
                    amount,
                    ccy: show,
                    formatQty,
                    className: row.depth === 0 ? 'font-semibold' : ''
                });
            },
            footer: () => {
                return <div />;
            }
        },
        {
            accessorKey: 'amountInBaseCurrency',
            header: `${lang.balances.balance} ${baseCurrency ? `(${baseCurrency})` : ''}`,
            cell: ({ row, getValue }: CellContext<PortalBalancesRow, any>) => {
                const value = getValue();
                const { show } = getCurrency(baseCurrency);

                return generalAmountCell({
                    amount: value,
                    ccy: show,
                    formatQty,
                    className: row.depth === 0 ? 'font-semibold' : ''
                });
            },
            footer: () => {
                const { show } = getCurrency(baseCurrency);

                return generalAmountCell({
                    amount: totalInBaseCurrency,
                    ccy: show,
                    formatQty,
                    className: 'font-semibold'
                });
            }
        },
        {
            accessorKey: 'deposit-withdrawal',
            header: lang.portal.actions,
            cell: ({ row, getValue }: CellContext<PortalBalancesRow, any>) => {
                const navigate = useNavigate();
                const type = currencyConfigDict[row.original.tableCurrency.show]?.type;

                return (
                    ((type === 'Crypto' && isCryptoDepositWithdrawalEnabled) || type !== 'Crypto') && (
                        <div className="flex items-center gap-2">
                            <MdArrowCircleDown
                                title="Deposit"
                                className="hover:text-brand-primary-light w-4 h-4 lg:w-5 lg:h-5"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/portal/deposits?currency=${row.original.tableCurrency.show}`);
                                    onDeposit?.(row.original.tableCurrency.show);
                                }}
                            />
                            <MdArrowCircleUp
                                title="Withdraw"
                                className="hover:text-brand-secondary-light w-4 h-4 lg:w-5 lg:h-5"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/portal/withdrawals?currency=${row.original.tableCurrency.show}`);
                                    onWithdraw?.(row.original.tableCurrency.show);
                                }}
                            />
                        </div>
                    )
                );
            },
            footer: () => {
                return <div />;
            }
        }
    ];
    return columns;
};
